import {createSlice, PayloadAction, Reducer} from '@reduxjs/toolkit';
import produce from 'immer';
import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {refreshAnalysis} from 'analysis-tool/polling/refreshPipeline';
import {assert} from 'Asserts';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {addAndUpdatePipelineSteps, UpdateStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStep} from 'pipelines/PipelineTypes';
import {SliceActions} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';
import {JSONValue} from 'util/Types';

import {ConfigFormFieldEvent, DataLayout} from '../DataLayoutTypes';
import pipelineLayoutStepToDataLayout from '../pipelineLayoutStepToDataLayout';
import {getAtLayoutPath, setAtLayoutPath} from '../utils/LayoutPathUtils';
import {AnalysisConfigFormField, AnalysisConfigReference} from './AnalysisAdapterTypes';
import {resolveConfigReference} from './AnalysisAdapterUtils';

interface UseHandleAnalysisLayoutEventsInput {
  analysis: PipelineGraphQL;
  clientStateId: string;
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>;
}

interface UseHandleAnalysisLayoutEventsOutput {
  handleOnEvents: (events: Array<ConfigFormFieldEvent>) => void;
  layout: DataLayout;
}

/**
 * Define callback handler for DataLayout's onEvents, and returns a `DataLayout` converted from an analysis layout with optimistic updates applied to it.
 *
 * The goal of this hook is to handle events from layouts in a smooth way. The base assumption is that events will be triggered faster than they're handled.
 * e.g. typing the string "3.14159" - that's 7 events in quick succession. Specifically, the hook only allows a single update request to be made at once,
 * even while user actions can still be adding actions.
 *
 * The hook solves this problem like so:
 * 1. It uses a queue to track events, so each `onEvents` call enqueues the events to it.
 * 2. When the queue changes, we start processing all events in the queue. To process events, we:
 *    a. Update all the steps affected at once.
 *    b. Flatten all the updates to the same step together into 1 update per step.
 *    c. Trigger a web request.
 *    d. Remove the processed events from the queue.
 * 3. If the queue changes while we're processing events, we'll skip processing them. Upon step 2d completing, we'll automatically check if any new events have been added.
 *
 * Notably, the first update request will always only contain the update from the very first event. An alternative approach would have been to wait until no new
 * events are added before sending the first update. While this would work better for typing events, it would be worse for click-based inputs (e.g. dropdowns),
 * and we anticipate the latter is more important for layouts.
 *
 * The hook additionally returns a layout which is optimistically updated in the following ways:
 *   - Config changes in the queue are applied, so that users can instantly see the updates they make to forms.
 *   - The views that contain the steps that are affected by the config changes will be set to RUNNING, so
 *   that users can instantly see data blocks respond to changes in the forms.
 *
 * We store the state of the queue in redux, so it's persisted across remounts - e.g. the user navigates to a different layout and back. One weakness with this is
 * that if they never navigate back to the analysis, events will never be processed. This is tolerable because event processing does happen fast, making this rare,
 * and the impact of dropping events should be minimal. A full solution for this would be to use background workers instead of a hook/react-render based approach.
 */
export default function useHandleAnalysisLayoutEvents({
  analysis,
  clientStateId,
  layoutStep,
}: UseHandleAnalysisLayoutEventsInput): UseHandleAnalysisLayoutEventsOutput {
  const layoutLayoutConfigEventQueueState = useSelector(
    (state: BenchlingState) => state.ui.analysisTool.layoutConfigEventQueue
  );
  const layoutConfigEventQueue = useMemo(
    () => layoutLayoutConfigEventQueueState[clientStateId]?.queue ?? [],
    [clientStateId, layoutLayoutConfigEventQueueState]
  );
  const isProcessingEvents = layoutLayoutConfigEventQueueState[clientStateId]?.isProcessing ?? false;
  const dispatch = useDispatch();

  const processEventQueue = useCallback(async (): Promise<void> => {
    if (isProcessingEvents || layoutConfigEventQueue.length === 0) {
      return;
    }

    const eventsToProcess = layoutConfigEventQueue;
    dispatch(setIsProcessing({clientStateId, isProcessing: true}));

    try {
      await processEvents(eventsToProcess, layoutStep, analysis);
    } finally {
      // Always remove events, so that events that cause errors don't infinitely retry. When errors happen,
      // a user is likely to try to click something to fix it anyway, which will retrigger event processing
      dispatch(removeConfigEvents({clientStateId, count: eventsToProcess.length}));
      dispatch(setIsProcessing({clientStateId, isProcessing: false}));
    }
  }, [analysis, clientStateId, layoutConfigEventQueue, isProcessingEvents, dispatch, layoutStep]);

  useEffect(() => {
    processEventQueue();
  }, [processEventQueue]);

  const layout = useMemo(() => {
    const updatedAnalysis = optimisticallyUpdateAnalysis(layoutConfigEventQueue, layoutStep, analysis);
    return layoutConfigEventQueue.reduce(
      (layout, event) => optimisticallyUpdateLayout(layout, event),
      pipelineLayoutStepToDataLayout(layoutStep, updatedAnalysis)
    );
  }, [analysis, layoutConfigEventQueue, layoutStep]);

  return {
    handleOnEvents: (events: Array<ConfigFormFieldEvent>) => {
      if (events.length > 0) {
        dispatch(addConfigEvents({clientStateId, events}));
      }
    },
    layout,
  };
}

export interface LayoutConfigEventQueueState {
  [clientStateId: string]: {
    queue: Array<ConfigFormFieldEvent>;
    isProcessing: boolean;
  };
}

/**
 * Store state of `useHandleAnalysisLayoutEvents`'s queue of config form field events.
 *
 * We use redux instead of `makeUseGlobalClientState` to track the queue because we need the ability to dispatch a mutation that says "add this to the queue"
 * instead of "here's the new queue". `makeUseGlobalClientState` doesn't support the latter (compared to `useState`). This motivating issue is that with complex
 * user input-based state, it's highly likely that many mutations happen in quick succession, so a given closure might not have the updated state when it creates
 * a mutation. The `setState` behavior always requires setting the entire final state, so it's possible that race conditions happen in this situation.
 */
const layoutConfigEventQueue = createSlice({
  name: 'layoutConfigEventQueue',
  initialState: {} as LayoutConfigEventQueueState,
  reducers: {
    addConfigEvents: (
      state,
      {
        payload: {clientStateId, events},
      }: PayloadAction<{clientStateId: string; events: Array<ConfigFormFieldEvent>}>
    ) => {
      state[clientStateId] = {
        queue: [...(state[clientStateId]?.queue ?? []), ...events],
        isProcessing: state[clientStateId]?.isProcessing ?? false,
      };
    },
    removeConfigEvents: (
      state,
      {payload: {clientStateId, count}}: PayloadAction<{clientStateId: string; count: number}>
    ) => {
      if (state[clientStateId] != null) {
        state[clientStateId] = {...state[clientStateId], queue: state[clientStateId].queue.slice(count)};
      }
    },
    setIsProcessing: (
      state,
      {payload: {clientStateId, isProcessing}}: PayloadAction<{clientStateId: string; isProcessing: boolean}>
    ) => {
      if (state[clientStateId] != null) {
        state[clientStateId] = {...state[clientStateId], isProcessing};
      }
    },
  },
});

const {addConfigEvents, removeConfigEvents, setIsProcessing} = layoutConfigEventQueue.actions;

export type LayoutConfigEventQueueAction = SliceActions<typeof layoutConfigEventQueue.actions>;
export const reduceLayoutConfigEventQueue = layoutConfigEventQueue.reducer as Reducer<
  LayoutConfigEventQueueState,
  LayoutConfigEventQueueAction
>;

function getResolvedEventsByStepId(
  events: Array<ConfigFormFieldEvent>,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: PipelineGraphQL
): Record<string, {step: PipelineStep; events: Array<ResolvedConfigFormFieldEvent>}> {
  const resolvedEvents = events.flatMap((event) => resolveConfigEvent(event, layoutStep, analysis));
  return resolvedEvents.reduce<
    Record<string, {step: PipelineStep; events: Array<ResolvedConfigFormFieldEvent>}>
  >((accumulatedEvents, resolvedEvent) => {
    const {step} = resolvedEvent;
    return {
      ...accumulatedEvents,
      [step.id]: {step, events: [...(accumulatedEvents[step.id]?.events || []), resolvedEvent]},
    };
  }, {});
}

// TypeScript needs us to make ReturnType<> an interface before we can extend it
type IResolveConfigAndEvent = ConfigFormFieldEvent & ReturnType<typeof resolveConfigReference>;
interface ResolvedConfigFormFieldEvent extends IResolveConfigAndEvent {
  analysisConfigFormField: AnalysisConfigFormField;
  configReference: AnalysisConfigReference;
}

function resolveConfigEvent(
  event: ConfigFormFieldEvent,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: PipelineGraphQL
): Array<ResolvedConfigFormFieldEvent> {
  assert(layoutStep.config != null, 'Layout step config is null');
  const {layoutPath} = event;
  const analysisConfigFormField = getAtLayoutPath<AnalysisConfigFormField>(layoutStep.config, layoutPath);

  return analysisConfigFormField.configReferences.map((configReference) => ({
    ...event,
    ...resolveConfigReference(configReference, layoutStep, analysis),
    analysisConfigFormField,
    configReference,
  }));
}

async function processEvents(
  events: Array<ConfigFormFieldEvent>,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: PipelineGraphQL
): Promise<void> {
  // Make a map of stepId to all the events that update it, so we can make one update per step.
  const resolvedEventsByStepId = getResolvedEventsByStepId(events, layoutStep, analysis);

  const stepUpdates = Object.values(resolvedEventsByStepId).map(({step, events}) =>
    makeStepUpdate(step, events)
  );

  await addAndUpdatePipelineSteps(analysis.id, {
    stepsToAdd: [],
    stepsToUpdate: stepUpdates,
  });
  await refreshAnalysis(analysis.id);
}

function makeStepUpdate(
  step: PipelineStep,
  resolvedEvents: Array<ResolvedConfigFormFieldEvent>
): UpdateStepParams {
  const config = step.config;
  assert(config != null, 'Unexpected null config');

  return {
    stepId: step.id,
    config: resolvedEvents.reduce(
      (config, event) => applyEventUpdateToConfig(config, event),
      step.config as Record<string, JSONValue>
    ),
    rerunAfterUpdate: true,
  };
}

function applyEventUpdateToConfig(
  config: Record<string, JSONValue>,
  {configReference: {configPath}, newValue}: ResolvedConfigFormFieldEvent
): Record<string, JSONValue> {
  return produce(config, (draftConfig) => {
    setAtLayoutPath(draftConfig, configPath, newValue);
  });
}

function optimisticallyUpdateAnalysis(
  events: Array<ConfigFormFieldEvent>,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: PipelineGraphQL
): PipelineGraphQL {
  const resolvedEventsByStepId = getResolvedEventsByStepId(events, layoutStep, analysis);
  const stepsToBeRun = new Set(Object.keys(resolvedEventsByStepId));

  return produce(analysis, (draftAnalysis) => {
    draftAnalysis.views.forEach((view) => {
      if (view.lineage.some((lineageNode) => stepsToBeRun.has(lineageNode.step.id))) {
        view.lineage[view.lineage.length - 1].step.statusCode = PipelineStepStatusCode.RUNNING;
      }
    });
  });
}

function optimisticallyUpdateLayout(
  layout: DataLayout,
  {layoutPath, newValue}: ConfigFormFieldEvent
): DataLayout {
  return produce(layout, (draftLayout) => {
    const configFormField = getAtLayoutPath<{config: unknown}>(draftLayout, layoutPath);
    assert(
      typeof configFormField === 'object' &&
        configFormField != null &&
        configFormField.hasOwnProperty('config'),
      `Invalid layout path`,
      {layout, layoutPath}
    );
    configFormField.config = newValue;
  });
}
